import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Download, Send, Sparkles, ArrowRight } from "lucide-react";
import TimezoneClock from "@/components/TimezoneClock";

/**
 * Phase 1E.1 UI polish — minimalist public landing.
 *
 * Inspired by the user-supplied reference aesthetic: a big headline,
 * one supporting sentence, and 2–3 pill-shaped CTAs. The full feature
 * inventory lives behind the dashboard — the landing's only job is
 * to get a new visitor through the door.
 */
export default function Landing() {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="px-6 md:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-gray-900 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
          </div>
          <span className="font-display font-semibold text-lg text-gray-900">
            WeDesi Lab
          </span>
        </div>
        <TimezoneClock />
      </header>

      {/* Hero */}
      <main className="flex-1 flex items-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
          <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight text-gray-900 leading-tight mb-6">
            Every feature we&rsquo;re building,
            <br />
            <span className="text-gray-400">in one quiet sandbox.</span>
          </h1>

          <p className="text-lg md:text-xl text-gray-500 mb-10 max-w-2xl mx-auto leading-relaxed">
            The WeDesi Lab is a working preview of the platform — identity
            board, festivals, institutions, events, rewards, and more.
            Sign in to explore.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <PillButton
              to={isAuthenticated ? "/" : "/login"}
              icon={<ArrowRight className="w-4 h-4" />}
              primary
            >
              {isAuthenticated ? "Open dashboard" : "Get started"}
            </PillButton>
            <PillButton
              href="https://github.com/pmk2429/wedesi-web"
              icon={<Download className="w-4 h-4" />}
            >
              Resources
            </PillButton>
            <PillButton
              href="mailto:hello@wedeshi.io"
              icon={<Send className="w-4 h-4" />}
            >
              Submit feedback
            </PillButton>
          </div>
        </div>
      </main>

      <footer className="px-6 md:px-10 py-6 text-xs text-gray-400">
        WeDesi Lab — internal preview · all features back working endpoints
      </footer>
    </div>
  );
}

function PillButton({
  to,
  href,
  children,
  icon,
  primary,
}: {
  to?: string;
  href?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  primary?: boolean;
}) {
  const base =
    "inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all";
  const variant = primary
    ? "bg-gray-900 text-white hover:bg-gray-700"
    : "bg-white text-gray-900 border border-gray-200 hover:border-gray-300";
  const className = `${base} ${variant}`;

  if (to) {
    return (
      <Link to={to} className={className}>
        {icon}
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} target="_blank" rel="noreferrer">
      {icon}
      {children}
    </a>
  );
}
